// The Proper Pet — one-time scroll reveal. See DESIGN.md "Motion".
// html.js only applies the hidden starting state (proper-pet-tokens.css),
// so any failure path here should reveal immediately rather than leave
// content stuck at opacity:0.
(function () {
  var SELECTOR = '[data-pp-reveal]';

  function revealAll(list) {
    Array.prototype.forEach.call(list, function (el) {
      el.classList.add('is-visible');
    });
  }

  // Pages built from Horizon's own sections (collection grid, cart, 404)
  // carry no data-pp-reveal, because native blocks have no way to emit a
  // custom attribute — the same limitation that made proper-page-intro.liquid
  // necessary for About/FAQ/Contact. Those pages had zero entrance motion,
  // measured on the delivered HTML (collection/cart/404: 0 occurrences).
  //
  // Adopting them here is safe only because of the viewport test below:
  // an element already on screen at load is left completely alone, so it
  // paints normally and can never flash (visible, then hidden by the CSS
  // gate, then animated back in). Only elements below the fold are opted in,
  // and those have not painted yet, so adding the attribute costs nothing.
  function adoptNativeTargets() {
    // Measured 14 August 2026 across homepage, product, article, collection,
    // cart and 404, at 1440: `.product-grid__item` matched 10 and
    // `.card-gallery` 18, so those two do the work. `.cart-items__item`
    // matched nothing only because the test cart was empty, so it stays.
    //
    // Two were removed rather than left to look busy:
    // `.collection-list__item` is Horizon's collection-list component, which
    // this store does not render, and `.section-404 .button` targets a class
    // that does not exist in this build at all - the 404's button carries
    // `button button--<hash>`, and the page has zero reveal targets as a
    // result. Replacing it would mean either a hashed class or a site-wide
    // `.button` rule that adds entrance motion to every button on the site,
    // which is a design decision rather than a repair. The 404 keeps the
    // behaviour it has always had, and the code stops claiming otherwise.
    var groups = ['.product-grid__item', '.card-gallery', '.cart-items__item'];
    var vh = window.innerHeight || document.documentElement.clientHeight;

    groups.forEach(function (sel) {
      var found = document.querySelectorAll(sel);
      Array.prototype.forEach.call(found, function (el, i) {
        if (el.hasAttribute('data-pp-reveal')) return;
        var rect = el.getBoundingClientRect();
        var alreadyOnScreen = rect.top < vh && rect.bottom > 0;
        if (alreadyOnScreen) return; // never risk a flash on painted content
        el.setAttribute('data-pp-reveal', '');
        el.style.setProperty('--pp-stagger-i', String(i % 4));
      });
    });
  }

  try {
    adoptNativeTargets();
  } catch (e) {
    // Adoption is an enhancement; never let it stop the real observer below.
  }

  var targets = document.querySelectorAll(SELECTOR);
  if (!targets.length) return;

  if (!('IntersectionObserver' in window)) {
    revealAll(targets);
    return;
  }

  try {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });

    // Time-based backstop. The viewport net below only fires when the window
    // reports 0x0, and there are real conditions where the size is fine and
    // the observer still never delivers: a document in a hidden or background
    // tab (Chrome does not run IntersectionObserver callbacks there), and a
    // headless full-page capture, where the renderer resizes after load.
    //
    // Measured on this store: a full-page mobile capture of the product page
    // came back **77% blank**, with one unbroken white run of 9,160px, while
    // the HTML underneath was complete and correct.
    //
    // The header of this file already states the rule - any failure path
    // should reveal rather than leave content at opacity:0 - and this closes
    // the last path that did not. Five seconds is far longer than any real
    // scroll takes to reach the second section, so a visible visitor still
    // sees the animation; anything that has not been reached by then was
    // never going to animate anyway.
    window.setTimeout(function () {
      revealAll(document.querySelectorAll(SELECTOR + ':not(.is-visible)'));
    }, 5000);

    // Safety net for a degenerate first layout: if the document reports no
    // usable viewport at script time (an embedded/hidden pane can report
    // 0x0), the observer computes nothing as intersecting and every target
    // stays stuck at opacity:0. Re-check once the window has a real size.
    // Found live: a preview pane that loaded at 0x0 left all 33 targets
    // hidden and the page looked broken while the markup was correct.
    if (!window.innerHeight || !window.innerWidth) {
      window.addEventListener(
        'resize',
        function onFirstRealSize() {
          if (!window.innerHeight) return;
          window.removeEventListener('resize', onFirstRealSize);
          Array.prototype.forEach.call(
            document.querySelectorAll(SELECTOR + ':not(.is-visible)'),
            function (el) {
              observer.observe(el);
            }
          );
        },
        { passive: true }
      );
    }
  } catch (e) {
    revealAll(targets);
  }
})();

/* Button label roll.
 *
 * Eivora's buttons hold TWO copies of the label stacked inside a masked
 * wrapper - `<p class="button-text">` plus `<p class="button-text absolute">`
 * sitting one line below - and on hover both translate up, so the label rolls
 * out of the top while its duplicate arrives from the bottom. Their measured
 * transition is 0.3s.
 *
 * Doing that in markup would mean editing every section that renders a
 * button. Doing it here wraps the label once at runtime for every
 * `.pp-btn-chip` on the page, so the interaction is defined in one place and
 * new buttons get it for free.
 *
 * Only the leading text node is wrapped: the trailing chip element is left
 * alone, and buttons whose label is already wrapped are skipped, so this is
 * safe to run again after a section re-renders in the theme editor.
 */
(function () {
  var DONE = 'data-pp-roll-ready';

  function wrap(btn) {
    if (btn.hasAttribute(DONE)) return;
    btn.setAttribute(DONE, '');

    var textNode = null;
    for (var i = 0; i < btn.childNodes.length; i++) {
      var n = btn.childNodes[i];
      if (n.nodeType === 3 && n.textContent.trim()) { textNode = n; break; }
    }
    if (!textNode) return;

    var label = textNode.textContent.trim();
    var mask = document.createElement('span');
    mask.className = 'pp-btn-chip__roll';

    var track = document.createElement('span');
    track.className = 'pp-btn-chip__roll-track';

    var a = document.createElement('span');
    a.textContent = label;
    var b = document.createElement('span');
    b.textContent = label;
    // The duplicate is decorative - a screen reader should hear the label once.
    b.setAttribute('aria-hidden', 'true');

    track.appendChild(a);
    track.appendChild(b);
    mask.appendChild(track);
    btn.replaceChild(mask, textNode);
  }

  function run() {
    Array.prototype.forEach.call(document.querySelectorAll('.pp-btn-chip'), wrap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', run);
})();
